import { useTranslation } from "react-i18next";

import { type JobRun, useJobRunLogs } from "~/components/job-log";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/components/ui";
import { cn } from "~/lib/utils";

function LogLines({
  jobRun,
  dryRun = false,
  onFetchResult,
}: {
  jobRun: JobRun;
  dryRun?: boolean;
  onFetchResult?: () => void;
}) {
  const { t } = useTranslation();
  const { data, logSource } = useJobRunLogs(jobRun);
  const jobDone = data.some((message) => message.message.includes("[SUCCEEDED]"));

  return (
    <section className="space-y-3">
      {dryRun && onFetchResult ? (
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={!jobDone} onClick={onFetchResult}>
            {t("ui.fetchX", { x: t("ui.result") })}
          </Button>
        </div>
      ) : null}
      <div className="max-h-[70vh] overflow-auto rounded-[var(--radius-md)] border border-border-default bg-[#1e1e1e] p-3 font-mono text-[12px] text-[#f8f8f8]">
        {data.length === 0 ? (
          <p className="text-fg-disabled">{t("feature.common.noData")}</p>
        ) : (
          <div className="grid min-w-max gap-0.5">
            {data.map((message, index) => (
              <div key={message.messageId} className="grid grid-cols-[56px_76px_64px_minmax(0,1fr)] gap-3">
                <span className="text-right text-[#999]">{index + 1}</span>
                <span>{message.time ? new Date(message.time).toLocaleTimeString() : ""}</span>
                <span
                  className={cn(
                    message.event === "error" && "text-score-c",
                    message.event === "warn" && "text-[#ff9900]",
                    message.event === "info" && "text-score-a",
                    !["error", "warn", "info"].includes(message.event) && "text-[#00ffff]",
                  )}
                >
                  {message.event}
                </span>
                <span className="whitespace-pre">{message.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-right text-12-regular text-fg-3">
        {t("ui.logSource")}: {logSource}
      </p>
    </section>
  );
}

export function JobLogDialog({
  jobRun,
  onOpenChange,
  onFetchResult,
}: {
  jobRun: JobRun | null;
  onOpenChange: (open: boolean) => void;
  onFetchResult?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={jobRun !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("model.batchEvidencesJobRun.modelName")}</DialogTitle>
          {jobRun ? (
            <DialogDescription>
              {t("model.common.id")}: {jobRun.jobRunId}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {jobRun ? <LogLines jobRun={jobRun} dryRun={jobRun.dryRun} onFetchResult={onFetchResult} /> : null}
      </DialogContent>
    </Dialog>
  );
}
